/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { injectIntl } from 'react-intl'

import { EuiText, EuiSpacer, EuiIcon, EuiFlexGroup, EuiFlexItem, EuiPageHeader } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiRouterLinkButton } from '@modules/cui/RouterButton'
import PrivacySensitiveContainer from '@modules/cui/PrivacySensitiveContainer'
import Breadcrumbs from '@modules/cui/Breadcrumbs'
import { organizationOverviewUrl } from '@modules/urls/organizations'
import { userOverviewUrl } from '@modules/urls/users'
import CopyButton from '@modules/cui/CopyButton'

import { getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { getDisplayName } from '@/lib/stackDeployments/selectors/display'

import DeploymentRegion from '../../StackDeploymentSearch/Deployment/DeploymentRegion'
import DeploymentLockGate from '../../DeploymentLockGate'
import StackDeploymentAdminActions from '../../StackDeployments/StackDeploymentAdminActions'
import { StackDeploymentHealthBadge } from '../../StackDeploymentHealthProblems/StackDeploymentHealthBadge'

import { useHeaderDefinition } from './headerDefinitions'

import type { AllProps } from './types'

// An <EuiText size='s'> sets a small line height, but in this case
// it impacts the horizontal alignment of the header.
const regionNameStyle = {
  lineHeight: `1.5rem`,
}

const DeploymentHeader: React.FunctionComponent<AllProps> = (props) => {
  const {
    deployment,
    stackDeployment,
    shouldShowRegion,
    hideClusterInsteadOfDelete,
    hideClusterInsteadOfStop,
    isOverviewPage,
    isHealthPage,
    problems,
  } = props

  const deploymentDisplayName = stackDeployment
    ? getDisplayName({ deployment: stackDeployment })
    : deployment.displayName

  const version = stackDeployment ? getVersion({ deployment: stackDeployment }) : undefined
  const {
    breadcrumbs,
    iconType,
    title,
    shouldHideRegion,
    hideHorizontalRule,
    component: Component,
  } = useHeaderDefinition({
    deploymentDisplayName,
    version,
  })

  const stackDeploymentVersion = stackDeployment?.id

  return (
    <React.Fragment>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      {Component ? (
        <Component {...props} problems={problems} />
      ) : (
        <EuiPageHeader
          bottomBorder={!hideHorizontalRule}
          pageTitle={
            <span data-test-id='deployment-title'>
              <PrivacySensitiveContainer nativeElementType='span'>
                {title}
              </PrivacySensitiveContainer>
            </span>
          }
          iconProps={{ size: 'l' }}
          iconType={iconType}
          rightSideGroupProps={{ gutterSize: 'm' }}
          rightSideItems={[
            renderUserLink(),
            isOverviewPage && (
              <DeploymentLockGate>
                <StackDeploymentAdminActions
                  deployment={stackDeployment!}
                  hideClusterInsteadOfDelete={hideClusterInsteadOfDelete}
                  hideClusterInsteadOfStop={hideClusterInsteadOfStop}
                />
              </DeploymentLockGate>
            ),
            /*
                renderUserLink can return null if lookupSaasUsers is false, when that happens the
                EuiPageHeader will complain if the rightSideItems prop has any null elements, so
                we need to remove it when that happens.
              */
          ].filter((item) => item !== null)}
        >
          {isHealthPage && (
            <StackDeploymentHealthBadge
              deploymentId={stackDeploymentVersion!}
              problems={problems}
              showViewIssuesLink={false}
            />
          )}
          {isOverviewPage && (
            <EuiFlexGroup gutterSize='s' direction='row' alignItems='center' responsive={false}>
              <EuiFlexItem grow={false}>
                <StackDeploymentHealthBadge
                  problems={problems}
                  deploymentId={stackDeploymentVersion!}
                  showViewIssuesLink={true}
                />
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <EuiText size='s' color='subdued'>
                  {/* Renders the unicode character for interpunct. */}
                  &#183;
                </EuiText>
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                {renderRegionIndicator(shouldShowRegion && !shouldHideRegion)}
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <EuiText size='s' color='subdued'>
                  {/* Renders the unicode character for interpunct. */}
                  &#183;
                </EuiText>
              </EuiFlexItem>

              <EuiFlexItem grow={false}>{renderDeploymentId()}</EuiFlexItem>
            </EuiFlexGroup>
          )}
        </EuiPageHeader>
      )}

      <EuiSpacer size='l' />
    </React.Fragment>
  )

  function renderUserLink() {
    const { lookupSaasUsers, deployment } = props

    if (!lookupSaasUsers) {
      return null
    }

    const {
      profile: { userId },
      settings: { metadata },
    } = deployment

    const organizationId = metadata?.organization_id

    if (organizationId !== undefined) {
      return (
        <CuiRouterLinkButton
          fill={true}
          to={organizationOverviewUrl(organizationId)}
          iconType='eye'
          data-test-id='organization-link'
        >
          <FormattedMessage
            id='deployment-summary.organization-link'
            defaultMessage='View organization'
          />
        </CuiRouterLinkButton>
      )
    }

    return (
      <EuiFlexItem grow={false}>
        <CuiRouterLinkButton fill={true} to={userOverviewUrl(userId)} iconType='eye'>
          <FormattedMessage id='deployment-summary.user-link' defaultMessage='View user' />
        </CuiRouterLinkButton>
      </EuiFlexItem>
    )
  }

  function renderRegionIndicator(shouldShowRegion: boolean) {
    if (!shouldShowRegion) {
      return null
    }

    const {
      deployment: { platform, regionId },
    } = props

    return (
      <EuiFlexGroup
        gutterSize='s'
        direction='row'
        alignItems='center'
        responsive={false}
        data-test-id='deployment-title-platform'
      >
        <EuiFlexItem grow={false}>
          <EuiIcon type='mapMarker' title={platform.id} />
        </EuiFlexItem>

        <EuiFlexItem>
          <EuiText size='s' color='subdued' style={regionNameStyle}>
            {platform.id.toUpperCase()}
            {' - '}
            <DeploymentRegion regionId={regionId} />
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  function renderDeploymentId() {
    const { id } = props.stackDeployment || {}

    if (!id) {
      return null
    }

    return (
      <EuiFlexGroup alignItems='center' gutterSize='s'>
        <EuiFlexItem grow={false}>
          <EuiText size='s' color='subdued' css={regionNameStyle}>
            <FormattedMessage
              id='deployment-summary.deployment-id'
              defaultMessage='Deployment ID'
            />
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup alignItems='center' gutterSize='xs'>
            <EuiFlexItem grow={false}>
              <EuiText size='s' css={regionNameStyle}>
                {id.slice(0, 6)}
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem grow={false} css={{ marginTop: -2 }}>
              <CopyButton asLink={true} value={id}>
                <EuiIcon type='copyClipboard' />
              </CopyButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }
}

export default injectIntl(DeploymentHeader)
